import { BackButton, Button, ClipSpinner } from '~/components/ui';
import { useTranslation } from 'react-i18next';
import {
  useGetOrderById,
  useGetPackingList,
  useReceiveOrder,
  useResendOrderPlacedEmail,
  useSyncOrderWithExternalVendor,
} from '~/api';
import { useGetOrderMsrPurchaseOrder } from '~/api/msrPurchaseOrder';
import { MsrPurchaseOrderCard } from '~/pages/order-view/sections/MsrPurchaseOrderCard';
import { msrSyncMarker, useMsrRefreshPolling } from '~/hooks/useMsrSyncPolling';
import { OrderStatusBadge } from '~/components/ui/OrderStatusBadge';
import {
  assertDefined,
  extractGraphQLErrorMessages,
  formatDateTime,
  sellerLabel,
  hasText,
  roundTo6dp,
  getLinePiecePrices,
  isRandomTallyItem,
} from '~/utils';
import { OrderProgressTrackerCard } from '~/components/cards/OrderProgessTrackerCard.tsx';
import { OrderSummaryCard } from '~/components/cards/OrderSummaryCard.tsx';
import { BlockedOrderCard } from '~/pages/order-view/sections/BlockedOrderCard';
import { UnprocessedBlockMessages } from '~/pages/order-view/sections/UnprocessedBlockMessages';
import { BuyerOrderCallToAction } from '~/pages/order-view/sections/BuyerCallToAction';
import { SellingInformationCard } from '~/components/cards/SellingInformationCard.tsx';
import { ShippingCard } from '~/components/cards/ShippingCard.tsx';
import { TarpStatus } from '~/components/ui/TarpStatus';
import { TransportationSection } from '~/pages/checkout/sections/TransportationSection';
import { OrderInternationalShippingCard } from '~/pages/order-view/sections/OrderInternationalShippingCard';
import { OrderDocumentsCard } from '~/pages/order-view/sections/OrderDocumentsCard';
import { ItemCard } from '~/components/cards/ItemCard';
import {
  type TransportMode,
  vehicleTypeToTransportMode,
} from '~/hooks/useTransportState';
import { PackingListSection } from '~/pages/order-view/sections/PackingListSection';
import { AddOrderContactButton } from '~/pages/order-view/sections/AddOrderContactButton';
import { ShipmentsSection } from '~/pages/order-view/sections/ShipmentsSection';
import { MsrPurchaseOrderModal } from '~/pages/order-view/sections/MsrPurchaseOrderModal';
import { MsrPurchaseOrderPrintOptionsPanel } from '~/pages/order-view/sections/MsrPurchaseOrderPrintOptionsPanel';
import { CheckCircle, ChevronDown } from 'lucide-react';
import { Popover } from 'react-tiny-popover';
import { CopySkippedItemsModal, Modal } from '~/components/overlays';
import { useId, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import toast from 'react-hot-toast';
import { useCopyOrderToCartAction } from '~/hooks/useCopyOrderToCartAction';
import {
  computeOrderItemsTotal,
  computePackedItemsTotal,
  getGradeInformation,
  getListedPricePerUnit,
  getUnprocessedBlockMessages,
  getVehiclePayload,
  groupItemsByTally,
} from '~/utils/order';
import type { OrderDocumentType } from '~/utils/documentType';
import { formatAddressText } from '~/utils/addressFormat';
import { facilityAddressLabel } from '~/utils/facilityOptions';
import { DeliveryAddressMapToggle } from '~/components/common/DeliveryAddressMapToggle';
import { useOrderStatusChangeToast } from '~/hooks/useOrderStatusChangeToast';
import { ALL_MSR_PRINT_OPTIONS } from '~/utils/msrPurchaseOrderUrl';

export function BuyerOrderView({ orderId }: Readonly<{ orderId: string }>) {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPurchaseOrderModalOpen, setIsPurchaseOrderModalOpen] =
    useState(false);
  const [isPrintOptionsOpen, setIsPrintOptionsOpen] = useState(false);
  const [printOptions, setPrintOptions] = useState(ALL_MSR_PRINT_OPTIONS);
  const [printTally, setPrintTally] = useState(true);
  const viewPurchaseOrderButtonRef = useRef<HTMLElement>(null);
  const printOptionsPanelId = useId();
  const printOptionsPanelRef = useRef<HTMLDivElement>(null);
  const { mutate: receiveOrder, isPending: isReceivingOrder } =
    useReceiveOrder();
  const isReceiveInFlight = useRef(false);
  const { mutate: resendOrderPlacedEmail, isPending: isResendingEmail } =
    useResendOrderPlacedEmail();
  const [searchParams] = useSearchParams();
  const {
    copyOrderToCart,
    pendingOrderIds,
    skippedItems,
    closeSkippedItemsModal,
  } = useCopyOrderToCartAction();

  const { t } = useTranslation('order');
  const { t: tOrderBuilder } = useTranslation('order-builder');

  // useGetOrderById's refetchInterval option needs the sync marker to detect the sync landing, but
  // the marker only exists in useGetOrderById's own result. lastKnownSyncMarker bridges the two,
  // adjusted during render (React's documented pattern) rather than via a ref, since a ref read
  // here would be a stale snapshot the compiler can't track across renders.
  const [lastKnownSyncMarker, setLastKnownSyncMarker] = useState<
    string | undefined
  >(undefined);
  const { refetchInterval, isRefreshing, startRefreshing } =
    useMsrRefreshPolling(lastKnownSyncMarker);
  const { data, isLoading, isError, error } = useGetOrderById(orderId, {
    refetchInterval,
  });
  const syncMarker = msrSyncMarker(data?.order);
  if (syncMarker !== lastKnownSyncMarker) {
    setLastKnownSyncMarker(syncMarker);
  }
  const { data: msrPurchaseOrderData } = useGetOrderMsrPurchaseOrder(
    orderId,
    data?.order?.msrSyncedAt
  );
  const msrPurchaseOrder =
    msrPurchaseOrderData?.order?.msrPurchaseOrder ?? null;
  const msrPurchaseOrderInputs = data?.order?.msrPurchaseOrderInputs ?? null;
  const { mutate: syncOrderWithMsr, isPending: isSyncingWithMsr } =
    useSyncOrderWithExternalVendor();
  const { data: packingListData } = useGetPackingList(orderId);
  const [activeItemsTab, setActiveItemsTab] = useState<'ordered' | 'packed'>(
    'ordered'
  );
  useOrderStatusChangeToast(data?.order?.status);

  if (isLoading) return <ClipSpinner />;
  if (isError)
    return <div>{t('order.loading_error', { message: error.message })}</div>;

  assertDefined(data?.order, 'Order data should be defined');
  const order = data.order;
  const canCopyToCart = order.parentOrder == null && !order.isBrokered;
  const msrPurchaseOrderNumber =
    order.canViewMsrPurchaseOrder && hasText(order.purchaseOrderNumber)
      ? order.purchaseOrderNumber
      : null;
  const unprocessedSyncMessages = getUnprocessedBlockMessages(
    order.blockMessages
  );

  const packingList = packingListData?.packingList ?? null;

  const currency = order.currency ?? 'USD';
  const originalTotal = computeOrderItemsTotal(order.items);
  const packedTotal =
    packingList != null ? computePackedItemsTotal(packingList.items) : 0;
  const variance = packedTotal - originalTotal;

  const orderDocuments: OrderDocumentType[] = order.documents;
  const hasUploadedPackingList = order.documents.some(
    (doc) =>
      doc.documentType === 'PACKING_LIST' && doc.uploadType === 'SELLER_UPLOAD'
  );

  const closePrintOptions = () => {
    setIsPrintOptionsOpen(false);
    viewPurchaseOrderButtonRef.current?.focus();
  };

  const closePrintOptionsOnClickOutside = () => {
    const focused = document.activeElement;
    const panel = printOptionsPanelRef.current;
    const isFocusInPanel = panel !== null && panel.contains(focused);
    if (focused === document.body || isFocusInPanel) {
      closePrintOptions();
    } else {
      setIsPrintOptionsOpen(false);
    }
  };

  const togglePrintOptions = () => {
    if (isPrintOptionsOpen) {
      closePrintOptions();
    } else {
      setIsPrintOptionsOpen(true);
    }
  };

  const closePurchaseOrderPdf = () => {
    setIsPurchaseOrderModalOpen(false);
    viewPurchaseOrderButtonRef.current?.focus();
  };

  const viewPurchaseOrderPdf = () => {
    closePrintOptions();
    setIsPurchaseOrderModalOpen(true);
  };

  const handleResendOrderPlacedEmail = () => {
    resendOrderPlacedEmail(
      { orderId: order.id },
      {
        onSuccess: () =>
          toast.success(t('order.resend_order_placed_email_success')),
        onError: () => {
          toast.error(t('order.resend_order_placed_email_error'));
        },
      }
    );
  };

  const handleSyncOrderWithMsr = () => {
    syncOrderWithMsr(
      { id: order.id },
      {
        onSuccess: () => {
          startRefreshing();
          toast.success(t('order.msr_sync_request_success'));
        },
        onError: (error) => {
          toast.error(
            t('order.msr_sync_request_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          );
        },
      }
    );
  };

  const handleReceiveOrder = () => {
    if (isReceiveInFlight.current) return;
    isReceiveInFlight.current = true;
    receiveOrder(
      { orderId: order.id },
      {
        onSuccess: () => {
          setIsConfirmModalOpen(false);
          toast.success(t('order.confirm_delivery_success'));
        },
        onError: (error) => {
          toast.error(
            t('order.confirm_delivery_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          );
        },
        onSettled: () => {
          isReceiveInFlight.current = false;
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
      >
        <DeliveryConfirmationDialog
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleReceiveOrder}
          isLoading={isReceivingOrder}
        />
      </Modal>
      <CopySkippedItemsModal
        skippedItems={skippedItems}
        onOkay={closeSkippedItemsModal}
      />
      {msrPurchaseOrderNumber !== null && (
        <MsrPurchaseOrderModal
          isOpen={isPurchaseOrderModalOpen}
          onClose={closePurchaseOrderPdf}
          orderId={order.id}
          printOptions={printOptions}
          purchaseOrderNumber={msrPurchaseOrderNumber}
          sellingOrganizationId={order.sellingOrganization.id}
          sellerContactIds={order.sellerContacts.map((contact) => contact.id)}
          primaryContactId={
            order.primaryContact ? order.primaryContact.id : undefined
          }
          purchaseOrderEmails={order.msrPurchaseOrderEmails}
        />
      )}
      <div>
        <BackButton
          link={searchParams.get('returnPath') ?? '/buyer-orders'}
          label={
            searchParams.get('returnPath') === '/dashboard'
              ? t('order.back_to_dashboard_label')
              : t('order.back_button_label')
          }
        />
        <div className="flex items-start justify-between">
          <div>
            <h1
              className="text-2xl sm:text-3xl text-neutral-900 mb-2"
              data-testid="order-heading"
            >
              {hasText(order.purchaseOrderNumber)
                ? order.purchaseOrderNumber
                : order.id}
            </h1>
            <p className="text-sm text-gray-600">
              {formatDateTime(order.createdAt)}
            </p>
            {order.canManuallySyncWithMsr && (
              <div className="flex items-center gap-2 mt-1">
                {hasText(order.purchaseOrderNumber) && (
                  <p className="text-sm text-gray-600">
                    {order.msrSyncedAt
                      ? t('order.msr_last_updated', {
                          date: formatDateTime(order.msrSyncedAt),
                        })
                      : t('order.msr_last_updated_never')}
                  </p>
                )}
                <Button
                  variant="ghost-primary"
                  size="sm"
                  width="auto"
                  loading={isSyncingWithMsr || isRefreshing}
                  onClick={handleSyncOrderWithMsr}
                  data-testid="sync-order-with-msr-button"
                >
                  {t('order.msr_sync_button')}
                </Button>
              </div>
            )}
            {order.canManuallySyncWithMsr &&
              unprocessedSyncMessages.length > 0 && (
                <UnprocessedBlockMessages
                  messages={unprocessedSyncMessages}
                  className="flex flex-col gap-1 mt-1"
                  itemClassName="text-xs text-warning-700"
                  data-testid="sync-order-messages"
                />
              )}
          </div>
          <div className="flex items-center gap-2">
            {order.canChoosePrimaryContact && (
              <AddOrderContactButton
                orderId={order.id}
                sellingOrganization={order.sellingOrganization}
              />
            )}
            {msrPurchaseOrderNumber !== null && (
              <Popover
                ref={viewPurchaseOrderButtonRef}
                isOpen={isPrintOptionsOpen}
                onClickOutside={closePrintOptionsOnClickOutside}
                positions={['bottom', 'top']}
                align="end"
                containerStyle={{ zIndex: '50' }}
                content={
                  <MsrPurchaseOrderPrintOptionsPanel
                    id={printOptionsPanelId}
                    ref={printOptionsPanelRef}
                    orderId={order.id}
                    purchaseOrderNotes={order.purchaseOrderNotes ?? ''}
                    purchaseOrderNotesFromMsr={order.purchaseOrderNotesFromMsr}
                    printOptions={printOptions}
                    onChange={setPrintOptions}
                    printTally={printTally}
                    onPrintTallyChange={setPrintTally}
                    onViewPdf={viewPurchaseOrderPdf}
                    onClose={closePrintOptions}
                  />
                }
              >
                <Button
                  size="sm"
                  width="auto"
                  variant="secondary"
                  aria-haspopup="dialog"
                  aria-expanded={isPrintOptionsOpen}
                  aria-controls={
                    isPrintOptionsOpen ? printOptionsPanelId : undefined
                  }
                  onClick={togglePrintOptions}
                  data-testid="view-purchase-order-button"
                >
                  {t('order.view_purchase_order_button')}
                  <ChevronDown size={16} aria-hidden="true" />
                </Button>
              </Popover>
            )}
            {canCopyToCart && (
              <Button
                size="sm"
                width="auto"
                loading={pendingOrderIds.has(order.id)}
                onClick={() => copyOrderToCart(order.id)}
                data-testid="copy-order-to-cart-button"
              >
                {t('order.copy_to_cart_button')}
              </Button>
            )}
            <OrderStatusBadge status={order.status} size="small" />
          </div>
        </div>
      </div>
      <div className="bg-neut-bg-alt p-6 rounded-lg flex flex-col gap-4 border border-neut-200 shadow-sm">
        <OrderProgressTrackerCard order={order} />
        {order.status === 'BLOCKED' && (
          <BlockedOrderCard
            orderId={order.id}
            blockMessages={order.blockMessages}
            canReturnToDraft={order.canReturnToDraft}
            showBuyerActions
          />
        )}
        <BuyerOrderCallToAction
          orderId={order.id}
          orderStatus={order.status}
          awaitingPurchaseOrderNumber={order.awaitingPurchaseOrderNumber}
        />
        {order.parentOrder == null &&
        order.shipments.length === 0 &&
        order.status?.toLowerCase() === 'shipped' ? (
          <Button onClick={() => setIsConfirmModalOpen(true)}>
            <CheckCircle size={16} /> {t('order.confirm_delivery_button')}
          </Button>
        ) : null}
      </div>

      {order.shipments.length > 0 && (
        <ShipmentsSection shipments={order.shipments} currency={currency} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-neut-0 rounded-lg border border-neut-200 shadow-sm">
            <div className="p-6 border-b border-neut-100">
              <h2 className="text-neutral-900 font-semibold">
                {t('order.order_items')}
              </h2>
              <TarpStatus tarp={order.tarp} />
            </div>
            {(packingList != null || order.status === 'CONFIRMED') &&
              !hasUploadedPackingList && (
                <div className="flex border-b border-gray-100 px-6">
                  <button
                    type="button"
                    onClick={() => setActiveItemsTab('ordered')}
                    className={`py-3 px-4 text-sm font-medium border-b-2 -mb-px ${
                      activeItemsTab === 'ordered'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('order.items_tab_ordered')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveItemsTab('packed')}
                    className={`py-3 px-4 text-sm font-medium border-b-2 -mb-px ${
                      activeItemsTab === 'packed'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t('order.items_tab_packed')}
                  </button>
                </div>
              )}
            {activeItemsTab === 'ordered' &&
              (() => {
                const vehicleId = order.transportVehicle?.id ?? '';
                const vehicleLabel: TransportMode | undefined =
                  order.transportVehicle
                    ? vehicleTypeToTransportMode(
                        order.transportVehicle.vehicleType
                      )
                    : undefined;
                const { tallyGroups, regularItems } = groupItemsByTally(
                  order.items
                );
                const renderItem = (item: (typeof order.items)[0]) => {
                  const ppl = item.platformProductsLengths;
                  const listedPrice = getListedPricePerUnit(
                    item,
                    order.incoTerm
                  );
                  const isRandomTally = isRandomTallyItem(item);
                  const { listedPricePerPiece, suggestedPerPiece } =
                    getLinePiecePrices(item, order.incoTerm);
                  return (
                    <ItemCard
                      key={ppl.id}
                      productName={ppl.platformProduct.name}
                      itemQuantity={item.quantity.value}
                      unit={item.quantity.unit}
                      pricePerUnit={listedPrice.amount}
                      currency={listedPrice.currency}
                      length={ppl.lengthDisplayValue}
                      gradeInformation={getGradeInformation(ppl)}
                      gradeLabel={t('order.grade')}
                      buyCode={item.buyCode}
                      suggestedPrice={
                        order.status === 'PENDING_SELLER_CONFIRMATION'
                          ? item.suggestedPrice?.amount
                          : undefined
                      }
                      buyerView
                      vehiclePayload={getVehiclePayload(
                        ppl.platformProductVehicleCapacities,
                        vehicleId
                      )}
                      vehicleLabel={vehicleLabel}
                      totalPieces={
                        isRandomTally ? undefined : item.quantityStored.value
                      }
                      pricePerPiece={
                        listedPricePerPiece != null
                          ? roundTo6dp(listedPricePerPiece.amount)
                          : undefined
                      }
                      pricePerPieceRaw={listedPricePerPiece?.amount}
                      suggestedPricePerPiece={
                        suggestedPerPiece != null
                          ? roundTo6dp(suggestedPerPiece.amount)
                          : undefined
                      }
                      suggestedPricePerPieceRaw={suggestedPerPiece?.amount}
                    />
                  );
                };
                return (
                  <>
                    {Array.from(tallyGroups.entries()).map(
                      ([groupKey, group]) => (
                        <div
                          key={groupKey}
                          className="border border-status-info-200 rounded-lg overflow-hidden mb-4 last:mb-0"
                        >
                          <div className="bg-status-info-50 px-4 sm:px-6 py-1.5 border-b border-status-info-200">
                            <span className="text-xs font-semibold text-status-info-800">
                              {tOrderBuilder(
                                group.isRandomTally
                                  ? 'order_builder.filters.random_tally'
                                  : 'order_builder.filters.canned_tally'
                              )}
                            </span>
                          </div>
                          {group.items.map(renderItem)}
                        </div>
                      )
                    )}
                    {regularItems.map(renderItem)}
                  </>
                );
              })()}
            {activeItemsTab === 'packed' && (
              <PackingListSection
                orderId={orderId}
                orderItems={order.items}
                orderStatus={order.status}
                currency={currency}
                useSellerAddFlow={false}
              />
            )}
          </div>
          {order.deliveryAddress ? (
            <ShippingCard
              shipFrom={facilityAddressLabel(order.sellingOrganizationFacility)}
              shippingLocationName={t('order.delivery_address')}
              addressLine1={
                order.deliveryAddress.addressLine1 ??
                formatAddressText(order.deliveryAddress)
              }
              addressLine2={order.deliveryAddress.addressLine2}
              region={order.deliveryAddress.region}
              city={order.deliveryAddress.city}
              zipCode={order.deliveryAddress.zipCode}
              incoTerm={order.incoTerm}
              mapToggle={
                <DeliveryAddressMapToggle address={order.deliveryAddress} />
              }
            />
          ) : (
            order.shippingLocation && (
              <ShippingCard
                shipFrom={facilityAddressLabel(
                  order.sellingOrganizationFacility
                )}
                shippingLocationName={order.shippingLocation.name}
                addressLine1={
                  order.shippingLocation.facility.address.addressLine1
                }
                addressLine2={
                  order.shippingLocation.facility.address.addressLine2
                }
                region={order.shippingLocation.facility.address.region}
                city={order.shippingLocation.facility.address.city}
                zipCode={order.shippingLocation.facility.address.zipCode}
                incoTerm={order.incoTerm}
                mapToggle={
                  <DeliveryAddressMapToggle
                    address={order.shippingLocation.facility.address}
                  />
                }
              />
            )
          )}
          {order.isInternational ? (
            <OrderInternationalShippingCard order={order} />
          ) : (
            order.status !== 'DRAFT' &&
            order.incoTerm && (
              <TransportationSection
                items={order.items}
                incoTerm={order.incoTerm}
                vehicle={order.transportVehicle}
              />
            )
          )}
          {(msrPurchaseOrder != null || msrPurchaseOrderInputs != null) && (
            <MsrPurchaseOrderCard
              purchaseOrder={msrPurchaseOrder}
              checkoutChoices={msrPurchaseOrderInputs}
            />
          )}
        </div>

        <div className="space-y-6">
          <SellingInformationCard
            sellingOrganizationName={sellerLabel(order.sellingOrganization)}
            sellingOrganizationFacility={facilityAddressLabel(
              order.sellingOrganizationFacility
            )}
            contacts={order.sellerContacts}
            primaryContactId={
              order.primaryContact ? order.primaryContact.id : undefined
            }
            onResendEmail={
              order.status === 'PENDING_SELLER_CONFIRMATION'
                ? handleResendOrderPlacedEmail
                : undefined
            }
            isResendingEmail={isResendingEmail}
            resendEmailDisabledReason={
              order.awaitingPurchaseOrderNumber
                ? t('order.resend_order_placed_email_held')
                : undefined
            }
          />
          <OrderSummaryCard
            items={order.items}
            status={order.status}
            currency={currency}
            incoTerm={order.incoTerm}
          />
        </div>
      </div>

      <OrderDocumentsCard
        orderId={orderId}
        documents={orderDocuments}
        packingList={packingList}
        orderItems={order.items}
        originalTotal={originalTotal}
        packedTotal={packedTotal}
        variance={variance}
        currency={currency}
      />
    </div>
  );
}

// Delivery Confirmation Dialog Component
interface DeliveryConfirmationDialogProps {
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function DeliveryConfirmationDialog({
  isLoading = false,
  onConfirm,
  onClose,
}: Readonly<DeliveryConfirmationDialogProps>) {
  const { t } = useTranslation('order');
  return (
    <div className="relative bg-neut-bg-alt rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
      {/* Success Icon */}
      <div className="w-12 h-12 rounded-full bg-success-200 flex items-center justify-center mb-4">
        <CheckCircle
          className="text-success-600"
          size={24}
          aria-hidden="true"
        />
      </div>

      {/* Dialog Content */}
      <h3 className="text-xl text-neut-prim font-semibold mb-2">
        {t('order.confirm_delivery_title')}
      </h3>

      <p className="text-sm text-neut-sec mb-6">
        {t('order.confirm_delivery_body')}
      </p>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button onClick={onClose} disabled={isLoading} variant="secondary">
          {t('order.confirm_delivery_keep')}
        </Button>

        <Button onClick={onConfirm} disabled={isLoading}>
          {isLoading
            ? t('order.confirm_delivery_confirming')
            : t('order.confirm_delivery_confirm_button')}
        </Button>
      </div>
    </div>
  );
}
